import { useState } from "react";
import {
  ActionIcon,
  Button,
  Divider,
  Group,
  Modal,
  Paper,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { IconEdit, IconPlus, IconTrash } from "@tabler/icons-react";
import dayjs from "dayjs";
import { api } from "@/lib/api";
import { formatAPIError, qk } from "@/lib/apiHelpers";
import { Can } from "@/components/Can";
import type { LoadingPoint, UnloadingPoint } from "@/types/api";

// ─── Loading Point Form ──────────────────────────────────────────────────────

interface LPFormValues {
  shipper_name: string;
  country: string;
  city: string;
  address: string;
  contact_person: string;
  contact_phone: string;
  loading_at: Date | null;
  loading_method: string;
  customs: string;
  notes: string;
}

interface UPFormValues {
  consignee_name: string;
  country: string;
  city: string;
  address: string;
  contact_person: string;
  contact_phone: string;
  unloading_at: Date | null;
  unloading_method: string;
  customs: string;
  notes: string;
}

function formatDateTime(s?: string): string {
  if (!s) return "—";
  const d = dayjs(s);
  return d.isValid() ? d.format("DD.MM.YYYY HH:mm") : "—";
}

// ─── LoadingSection ──────────────────────────────────────────────────────────

interface LoadingSectionProps {
  orderId: string;
}

function LoadingSection({ orderId }: LoadingSectionProps) {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<LoadingPoint | null>(null);

  const { data: points, isLoading } = useQuery({
    queryKey: qk.orderLoadingPoints(orderId),
    queryFn: async () => {
      const r = await api.get<LoadingPoint[]>(`/api/v1/orders/${orderId}/loading-points`);
      return Array.isArray(r.data) ? r.data : [];
    },
    staleTime: 0,
  });

  const form = useForm<LPFormValues>({
    initialValues: {
      shipper_name: "",
      country: "",
      city: "",
      address: "",
      contact_person: "",
      contact_phone: "",
      loading_at: null,
      loading_method: "",
      customs: "",
      notes: "",
    },
  });

  function openCreate() {
    setEditing(null);
    form.reset();
    setModalOpen(true);
  }

  function openEdit(p: LoadingPoint) {
    setEditing(p);
    form.setValues({
      shipper_name: p.shipper_name ?? "",
      country: p.country ?? "",
      city: p.city ?? "",
      address: p.address ?? "",
      contact_person: p.contact_person ?? "",
      contact_phone: p.contact_phone ?? "",
      loading_at: p.loading_at ? dayjs(p.loading_at).toDate() : null,
      loading_method: p.loading_method ?? "",
      customs: p.customs ?? "",
      notes: p.notes ?? "",
    });
    setModalOpen(true);
  }

  const saveMutation = useMutation({
    mutationFn: async (values: LPFormValues) => {
      const body = {
        ...values,
        loading_at: values.loading_at ? values.loading_at.toISOString() : undefined,
      };
      if (editing) {
        await api.patch(`/api/v1/orders/${orderId}/loading-points/${editing.id}`, body);
      } else {
        await api.post(`/api/v1/orders/${orderId}/loading-points`, body);
      }
    },
    onSuccess: () => {
      notifications.show({ color: "green", title: "Сохранено", message: "Точка погрузки сохранена" });
      qc.invalidateQueries({ queryKey: qk.orderLoadingPoints(orderId) });
      setModalOpen(false);
    },
    onError: (err) => {
      notifications.show({ color: "red", title: "Ошибка", message: formatAPIError(err) });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (pid: string) => {
      await api.delete(`/api/v1/orders/${orderId}/loading-points/${pid}`);
    },
    onSuccess: () => {
      notifications.show({ color: "green", title: "Удалено", message: "Точка погрузки удалена" });
      qc.invalidateQueries({ queryKey: qk.orderLoadingPoints(orderId) });
    },
    onError: (err) => {
      notifications.show({ color: "red", title: "Ошибка", message: formatAPIError(err) });
    },
  });

  const rows = points ?? [];

  return (
    <>
      <Stack gap="xs">
        <Group justify="space-between" align="center">
          <Text fw={600} size="sm">Погрузка</Text>
          <Can perm="orders:write">
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>
              Добавить
            </Button>
          </Can>
        </Group>

        {isLoading ? (
          <Text size="sm" c="dimmed">Загрузка…</Text>
        ) : rows.length === 0 ? (
          <Text size="sm" c="dimmed">Точки погрузки не добавлены.</Text>
        ) : (
          <Table striped withTableBorder withColumnBorders fz="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>#</Table.Th>
                <Table.Th>Грузоотправитель</Table.Th>
                <Table.Th>Страна / Город</Table.Th>
                <Table.Th>Адрес</Table.Th>
                <Table.Th>Контакт</Table.Th>
                <Table.Th>Дата погрузки</Table.Th>
                <Table.Th>Таможня</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((p, idx) => (
                <Table.Tr key={p.id}>
                  <Table.Td>{idx + 1}</Table.Td>
                  <Table.Td>{p.shipper_name || "—"}</Table.Td>
                  <Table.Td>{[p.country, p.city].filter(Boolean).join(" / ") || "—"}</Table.Td>
                  <Table.Td>{p.address || "—"}</Table.Td>
                  <Table.Td>
                    {p.contact_person || p.contact_phone
                      ? [p.contact_person, p.contact_phone].filter(Boolean).join(", ")
                      : "—"}
                  </Table.Td>
                  <Table.Td>{formatDateTime(p.loading_at)}</Table.Td>
                  <Table.Td>{p.customs || "—"}</Table.Td>
                  <Table.Td>
                    <Can perm="orders:write">
                      <Group gap={4} wrap="nowrap">
                        <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(p)}>
                          <IconEdit size={14} />
                        </ActionIcon>
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          color="red"
                          loading={deleteMutation.isPending}
                          onClick={() => deleteMutation.mutate(p.id)}
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    </Can>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? "Редактировать точку погрузки" : "Добавить точку погрузки"}
        size="lg"
      >
        <Stack gap="sm">
          <TextInput label="Грузоотправитель" {...form.getInputProps("shipper_name")} />
          <Group grow>
            <TextInput label="Страна" {...form.getInputProps("country")} />
            <TextInput label="Город" {...form.getInputProps("city")} />
          </Group>
          <TextInput label="Адрес" {...form.getInputProps("address")} />
          <Group grow>
            <TextInput label="Контактное лицо" {...form.getInputProps("contact_person")} />
            <TextInput label="Телефон" {...form.getInputProps("contact_phone")} />
          </Group>
          <Group grow>
            <DateTimePicker
              label="Дата и время погрузки"
              placeholder="дд.мм.гггг чч:мм"
              valueFormat="DD.MM.YYYY HH:mm"
              clearable
              {...form.getInputProps("loading_at")}
            />
            <TextInput label="Метод погрузки" {...form.getInputProps("loading_method")} />
          </Group>
          <TextInput label="Таможня / КПП" {...form.getInputProps("customs")} />
          <Textarea label="Примечания" minRows={2} {...form.getInputProps("notes")} />
          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={() => setModalOpen(false)}>Отмена</Button>
            <Button
              loading={saveMutation.isPending}
              onClick={() => saveMutation.mutate(form.values)}
            >
              Сохранить
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

// ─── UnloadingSection ────────────────────────────────────────────────────────

function UnloadingSection({ orderId }: LoadingSectionProps) {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<UnloadingPoint | null>(null);

  const { data: points, isLoading } = useQuery({
    queryKey: qk.orderUnloadingPoints(orderId),
    queryFn: async () => {
      const r = await api.get<UnloadingPoint[]>(`/api/v1/orders/${orderId}/unloading-points`);
      return Array.isArray(r.data) ? r.data : [];
    },
    staleTime: 0,
  });

  const form = useForm<UPFormValues>({
    initialValues: {
      consignee_name: "",
      country: "",
      city: "",
      address: "",
      contact_person: "",
      contact_phone: "",
      unloading_at: null,
      unloading_method: "",
      customs: "",
      notes: "",
    },
  });

  function openCreate() {
    setEditing(null);
    form.reset();
    setModalOpen(true);
  }

  function openEdit(p: UnloadingPoint) {
    setEditing(p);
    form.setValues({
      consignee_name: p.consignee_name ?? "",
      country: p.country ?? "",
      city: p.city ?? "",
      address: p.address ?? "",
      contact_person: p.contact_person ?? "",
      contact_phone: p.contact_phone ?? "",
      unloading_at: p.unloading_at ? dayjs(p.unloading_at).toDate() : null,
      unloading_method: p.unloading_method ?? "",
      customs: p.customs ?? "",
      notes: p.notes ?? "",
    });
    setModalOpen(true);
  }

  const saveMutation = useMutation({
    mutationFn: async (values: UPFormValues) => {
      const body = {
        ...values,
        unloading_at: values.unloading_at ? values.unloading_at.toISOString() : undefined,
      };
      if (editing) {
        await api.patch(`/api/v1/orders/${orderId}/unloading-points/${editing.id}`, body);
      } else {
        await api.post(`/api/v1/orders/${orderId}/unloading-points`, body);
      }
    },
    onSuccess: () => {
      notifications.show({ color: "green", title: "Сохранено", message: "Точка разгрузки сохранена" });
      qc.invalidateQueries({ queryKey: qk.orderUnloadingPoints(orderId) });
      setModalOpen(false);
    },
    onError: (err) => {
      notifications.show({ color: "red", title: "Ошибка", message: formatAPIError(err) });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (pid: string) => {
      await api.delete(`/api/v1/orders/${orderId}/unloading-points/${pid}`);
    },
    onSuccess: () => {
      notifications.show({ color: "green", title: "Удалено", message: "Точка разгрузки удалена" });
      qc.invalidateQueries({ queryKey: qk.orderUnloadingPoints(orderId) });
    },
    onError: (err) => {
      notifications.show({ color: "red", title: "Ошибка", message: formatAPIError(err) });
    },
  });

  const rows = points ?? [];

  return (
    <>
      <Stack gap="xs">
        <Group justify="space-between" align="center">
          <Text fw={600} size="sm">Разгрузка</Text>
          <Can perm="orders:write">
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>
              Добавить
            </Button>
          </Can>
        </Group>

        {isLoading ? (
          <Text size="sm" c="dimmed">Загрузка…</Text>
        ) : rows.length === 0 ? (
          <Text size="sm" c="dimmed">Точки разгрузки не добавлены.</Text>
        ) : (
          <Table striped withTableBorder withColumnBorders fz="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>#</Table.Th>
                <Table.Th>Грузополучатель</Table.Th>
                <Table.Th>Страна / Город</Table.Th>
                <Table.Th>Адрес</Table.Th>
                <Table.Th>Контакт</Table.Th>
                <Table.Th>Дата разгрузки</Table.Th>
                <Table.Th>Таможня</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((p, idx) => (
                <Table.Tr key={p.id}>
                  <Table.Td>{idx + 1}</Table.Td>
                  <Table.Td>{p.consignee_name || "—"}</Table.Td>
                  <Table.Td>{[p.country, p.city].filter(Boolean).join(" / ") || "—"}</Table.Td>
                  <Table.Td>{p.address || "—"}</Table.Td>
                  <Table.Td>
                    {p.contact_person || p.contact_phone
                      ? [p.contact_person, p.contact_phone].filter(Boolean).join(", ")
                      : "—"}
                  </Table.Td>
                  <Table.Td>{formatDateTime(p.unloading_at)}</Table.Td>
                  <Table.Td>{p.customs || "—"}</Table.Td>
                  <Table.Td>
                    <Can perm="orders:write">
                      <Group gap={4} wrap="nowrap">
                        <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(p)}>
                          <IconEdit size={14} />
                        </ActionIcon>
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          color="red"
                          loading={deleteMutation.isPending}
                          onClick={() => deleteMutation.mutate(p.id)}
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    </Can>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? "Редактировать точку разгрузки" : "Добавить точку разгрузки"}
        size="lg"
      >
        <Stack gap="sm">
          <TextInput label="Грузополучатель" {...form.getInputProps("consignee_name")} />
          <Group grow>
            <TextInput label="Страна" {...form.getInputProps("country")} />
            <TextInput label="Город" {...form.getInputProps("city")} />
          </Group>
          <TextInput label="Адрес" {...form.getInputProps("address")} />
          <Group grow>
            <TextInput label="Контактное лицо" {...form.getInputProps("contact_person")} />
            <TextInput label="Телефон" {...form.getInputProps("contact_phone")} />
          </Group>
          <Group grow>
            <DateTimePicker
              label="Дата и время разгрузки"
              placeholder="дд.мм.гггг чч:мм"
              valueFormat="DD.MM.YYYY HH:mm"
              clearable
              {...form.getInputProps("unloading_at")}
            />
            <TextInput label="Метод разгрузки" {...form.getInputProps("unloading_method")} />
          </Group>
          <TextInput label="Таможня / КПП" {...form.getInputProps("customs")} />
          <Textarea label="Примечания" minRows={2} {...form.getInputProps("notes")} />
          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={() => setModalOpen(false)}>Отмена</Button>
            <Button
              loading={saveMutation.isPending}
              onClick={() => saveMutation.mutate(form.values)}
            >
              Сохранить
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

// ─── LoadingPointsTab (exported) ─────────────────────────────────────────────

interface LoadingPointsTabProps {
  orderId?: string;
}

export function LoadingPointsTab({ orderId }: LoadingPointsTabProps) {
  if (!orderId) {
    return (
      <Paper p="xl" withBorder>
        <Text c="dimmed" ta="center" size="sm">
          Сначала сохраните заявку, затем добавьте погрузку.
        </Text>
      </Paper>
    );
  }

  return (
    <Paper p="md" withBorder>
      <Stack gap="lg">
        <Title order={5}>Погрузка / Разгрузка</Title>
        <LoadingSection orderId={orderId} />
        <Divider />
        <UnloadingSection orderId={orderId} />
      </Stack>
    </Paper>
  );
}
